import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3';
import {
  createRestyleComponent,
  createVariant,
  color,
  border,
  spacing,
  ColorProps,
  BorderProps,
  SpacingProps,
  VariantProps,
} from '@shopify/restyle';
import { ComponentProps, ReactNode } from 'react';
import { TouchableOpacity } from 'react-native';

import Highlighter from '@components/HighlightWords';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { useHover } from '@hooks/useHover';
import theme, { Theme } from '@themes/theme';
import {
  ellipsizeByCharCount,
  truncateForActivityFeed,
} from '@utils/ellipsizeByCharCount';
import { getChipHoverBackground } from '@utils/hoverStyles';

const maxChars = 24;

type RestyleProps = ColorProps<Theme> &
  BorderProps<Theme> &
  SpacingProps<Theme> &
  VariantProps<Theme, 'tagVariants'>;

const RestyleTag = createRestyleComponent<RestyleProps, Theme>([
  color,
  border,
  spacing,
  createVariant({ themeKey: 'tagVariants' }),
]);

export type Props = RestyleProps & {
  label: string;
  labelVariant?:
    | 'webSmall'
    | 'labelSmall'
    | 'metadata'
    | 'metadataSecondaryNew'
    | 'label'
    | 'labelCompletedTask'
    | 'labelAssignedTask';
  prefixMarginRight?: keyof typeof theme.spacing;
  isPill?: boolean;
  onDismiss?: () => void;
  onPress?: () => void;
  onLongPress?: () => void;
  prefix?: ReactNode;
  suffix?: ReactNode;
  filterVal?: string;
  type?: string;
  deletedAt?: string;
  isCompleted?: boolean;
  isFrom?: string;
  iconWidth?: number;
  iconHeight?: number;
  isMemberActivity?: boolean;
  isActivityItem?: boolean;
  /**
   * Full (un-truncated) text shown as a native browser tooltip on hover and
   * keyboard focus.  When omitted, the component derives the tooltip
   * automatically from `label` whenever the displayed text is truncated.
   */
  title?: string;
};

export const Tag: React.FC<Props> = ({
  label,
  labelVariant = 'metadata',
  onDismiss = undefined,
  prefix,
  prefixMarginRight,
  suffix,
  onPress,
  onLongPress,
  filterVal = '',
  type,
  deletedAt,
  isCompleted = false,
  isFrom,
  iconWidth,
  iconHeight,
  isMemberActivity = false,
  isActivityItem = false,
  title,
  ...rest
}: Props) => {
  const color: ComponentProps<typeof Text>['color'] =
    (theme.tagVariants[rest.variant as keyof typeof theme.tagVariants]
      ?.color as ComponentProps<typeof Text>['color']) ?? 'blue';

  // Chip hover rule (TA-9005): only interactive chips hover — the resting
  // background fades to 50% (colored) or gains grey01@50% (transparent/white).
  const isInteractive = !!onPress && !deletedAt;
  const { isHovered, hoverProps } = useHover({ disabled: !isInteractive });
  const restingBgToken = (
    theme.tagVariants[rest.variant as keyof typeof theme.tagVariants] as
      | { backgroundColor?: keyof typeof theme.colors }
      | undefined
  )?.backgroundColor;
  const hoverBackground = isHovered
    ? getChipHoverBackground(
        restingBgToken ? theme.colors[restingBgToken] : undefined
      )
    : undefined;

  const shouldTruncate =
    (isFrom === 'EditTags' && type !== 'tasks') || type === 'tasks';

  const getDisplayLabel = (): string => {
    if (!shouldTruncate) return label;
    if (isActivityItem && type === 'tasks')
      return truncateForActivityFeed(label);
    return ellipsizeByCharCount(label, maxChars);
  };
  const displayLabel = getDisplayLabel();

  // Determine whether text is actually clipped so we only activate the tooltip
  // when needed. When the caller pre-truncates before passing `label` (e.g.
  // project tags from TagsCollection), compare the explicit `title` prop against
  // `label`; otherwise compare the locally derived `displayLabel` against `label`.
  const isTruncated =
    title !== undefined ? title !== label : displayLabel !== label;
  // Full text for the tooltip — only defined when the tag is actually clipped.
  const effectiveTitle: string | undefined = isTruncated
    ? (title ?? label)
    : undefined;

  // Stable e2e hook for the remove control, mirroring Tag.tsx (the native
  // variant): the web build resolves THIS file, so the hook must live here too
  // or it never reaches the browser. Slugify the label for a deterministic,
  // platform-safe id. See TA-9805.
  const removeTestID = `tag-remove-${label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')}`;

  // Derive text style without nested ternaries (satisfies no-nested-ternary rule)
  const getTextStyle = (): object => {
    if (type === 'tasks' && deletedAt) {
      return {
        textDecorationLine: 'line-through',
        textDecorationStyle: 'solid',
      };
    }
    if (isActivityItem) {
      // Activity items use a compact layout; no shrink/alignment overrides needed
      return {};
    }
    // Preserve flexShrink/alignSelf for all other Tag usages (chat, project
    // views, join requests, etc.) to prevent text overflow
    return { flexShrink: 1, alignSelf: 'flex-start' };
  };

  return (
    <TooltipWebV3
      title={effectiveTitle ?? ''}
      disableHoverListener={!effectiveTitle}
      disableFocusListener={!effectiveTitle}
      disableTouchListener={!effectiveTitle}
      placement='bottom'>
      <TouchableOpacity
        {...hoverProps}
        disabled={deletedAt ? true : !onPress && !onDismiss}
        onPress={() => onPress?.()}
        onLongPress={onLongPress}
        accessibilityLabel={label}
        activeOpacity={onPress ? 0.2 : 1}
        style={{ cursor: onPress ? 'pointer' : 'default' }}>
        <RestyleTag
          {...rest}
          // RestyleTag's prop type omits `style`, but the underlying View
          // accepts it — needed for the non-token 50%-alpha hover colour.
          {...({
            style: hoverBackground
              ? { backgroundColor: hoverBackground }
              : undefined,
          } as object)}>
          {prefix && (
            <Box
              width={iconWidth ?? 16}
              height={iconHeight ?? 16}
              marginRight={prefixMarginRight || 'xs'}
              alignItems='center'
              justifyContent='center'>
              {prefix}
            </Box>
          )}

          <Text
            maxWidth={isMemberActivity ? 170 : undefined}
            variant={labelVariant}
            color={deletedAt ? 'grey04' : color}
            numberOfLines={1}
            ellipsizeMode='tail'
            style={getTextStyle()}>
            <Highlighter
              autoEscape
              highlightStyle={{
                color: theme.colors.black,
                backgroundColor: theme.colors.yellowBright,
                fontFamily: 'Inter_600SemiBold',
              }}
              searchWords={filterVal?.split(' ') || ''}
              textToHighlight={isMemberActivity ? label : displayLabel}
            />
          </Text>

          {suffix && !isCompleted && (
            <Box
              width={iconWidth ?? 16}
              height={iconHeight ?? 16}
              marginLeft='xs'
              alignItems='center'
              justifyContent='center'>
              {suffix}
            </Box>
          )}
          {!!onDismiss && (
            <TouchableOpacity
              testID={removeTestID}
              accessibilityLabel={`Remove ${label}`}
              onPress={onDismiss}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <Icon
                name='X2'
                marginLeft='xxs'
                color='black'
                variant='s'
                accessibilityLabel='Remove'
              />
            </TouchableOpacity>
          )}
          {isCompleted && (
            <Box
              flex={1}
              height={16}
              alignItems='flex-end'
              justifyContent='center'>
              {suffix}
            </Box>
          )}
        </RestyleTag>
      </TouchableOpacity>
    </TooltipWebV3>
  );
};

export default Tag;
